import React, { useState } from 'react';
import { 
  FileSpreadsheet,
  Calculator,
  Database,
  Home,
  ChevronDown,
  ChevronRight,
  Upload,
  TrendingUp,
  Target,
  Settings,
  History,
  Shield,
  Sparkles,
  FileText,
  AlertCircle,
  ClipboardList,
  Users,
  Building2,
  Activity,
  FileSignature,
  CheckCircle
} from 'lucide-react';
import ClientSwitcher from './ClientSwitcher';

const ProSidebar = ({ activeTab, setActiveTab, calculation }) => {
  const [legacyExpanded, setLegacyExpanded] = useState(false);
  
  // Check user role from localStorage. "adminOnly" navigation is for Chess
  // staff (super admins and consultants); client roles never see it.
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin' || user.role === 'consultant';

  const navigationItems = [
    {
      id: 'home',
      label: 'Home',
      icon: Home,
      description: 'Dashboard overview'
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: Building2,
      description: 'Manage clients & onboarding',
      adminOnly: true
    },
    {
      id: 'gl',
      label: 'General Ledger',
      icon: Database,
      description: 'View GL data'
    },
    {
      id: 'gl-calculations',
      label: 'ICS Schedule Calculations',
      icon: Calculator,
      description: 'Run calculations'
    },
    {
      id: 'schedule-h',
      label: 'Schedule H',
      icon: ClipboardList,
      description: 'Cost allocation template'
    },
    {
      id: 'schedule-e',
      label: 'Schedule E',
      icon: FileText,
      description: 'Allocation bases'
    },
    {
      id: 'schedule-k',
      label: 'Schedule K',
      icon: FileSpreadsheet,
      description: 'T&M Labor Hour Contracts'
    },
    {
      id: 'schedule-i',
      label: 'Schedule I',
      icon: FileSignature,
      description: 'Billing vs Costs Claimed'
    },
    {
      id: 'schedule-g',
      label: 'Schedule G',
      icon: FileText,
      description: 'Books-to-claimed direct costs'
    },
    {
      id: 'schedule-l',
      label: 'Schedule L',
      icon: FileText,
      description: 'Payroll to labor distribution'
    },
    {
      id: 'rules-debug',
      label: 'Rules Editor',
      icon: Settings,
      description: 'View & edit calculation rules'
    },
    {
      id: 'unmapped-accounts',
      label: 'Unmapped Accounts',
      icon: AlertCircle,
      description: 'Validate & map accounts'
    },
    {
      id: 'audit',
      label: 'DCAA Audit',
      icon: Shield,
      description: 'Audit checks & validation'
    },
    {
      id: 'client-closeout',
      label: 'Client Closeout',
      icon: CheckCircle,
      description: 'Adequacy review of the A-K export'
    },
    {
      id: 'reconciliation',
      label: 'Data Insights',
      icon: Activity,
      description: 'GL/Schedule H divergence alerts'
    },
    {
      id: 'contract-briefings',
      label: 'Contract Briefings',
      icon: FileSignature,
      description: 'AI-powered contract analysis'
    }
  ].filter(item => !item.adminOnly || isSuperAdmin); // Filter admin-only items

  const legacyItems = [
    { id: 'upload', label: 'Upload & Setup', icon: Upload },
    { id: 'rates', label: 'Rates & Summary', icon: Calculator },
    { id: 'schedules', label: 'ICE Schedules', icon: FileSpreadsheet },
    { id: 'adjustments', label: 'Manual Adjustments', icon: Settings },
    { id: 'variance', label: 'Variance Monitoring', icon: TrendingUp },
    { id: 'allocations', label: 'Advanced Allocations', icon: Target },
    { id: 'contracts', label: 'Contract Allocations', icon: FileText },
    { id: 'ai-assistant', label: 'AI Assistant', icon: Sparkles },
    { id: 'governance', label: 'Governance', icon: History }
  ];

  return (
    <div className="pro-sidebar" style={{ width: '256px', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Sidebar Header */}
      <div className="pro-sidebar-header">
        <div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: '700', color: 'var(--white)', letterSpacing: '-0.02em' }}>
            IncurredIQ
          </h2>
        </div>
      </div>

      {/* Client Switcher */}
      <ClientSwitcher />

      {/* Navigation Items */}
      <div style={{ flex: 1, padding: '0.5rem 0', overflowY: 'auto' }}>
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          return (
            <div
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`pro-sidebar-nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon style={{ width: '1.25rem', height: '1.25rem', marginRight: '0.75rem' }} />
              <div style={{ flex: 1 }}>
                <div>{item.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.125rem' }}>
                  {item.description}
                </div>
              </div>
            </div>
          );
        })}

      </div>

      {/* Footer */}
      <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--gray-200)' }}>
        {calculation ? (
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Active Calculation</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-900)', marginTop: '0.25rem', fontWeight: '500' }}>
              {calculation.filename || 'Active'}
            </p>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>No Active Calculation</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>
              Upload a file to begin
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProSidebar;
